// Lost and found's screens (PMU-236): staff log an item and hand it over; parents see what could be
// their children's and claim it. Shared by the staff app, the parent app and the console.
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Banner, Button, Dialog, DialogClose, EmptyState, Icon, Select, Spinner, TextField } from "@vavr/ui";
import { api, useBlobUrl } from "@vavr/screens/shared";
import { ReportDialog, RulesCard, useTerms } from "../safety/screens.tsx";
import "./lostfound.css";

export interface Item {
  id: string; category: string; description: string; found_where: string | null; route: string | null; on_bus: boolean; found_at: number; found_by?: string;
  status: "open" | "claimed" | "returned" | "disposed"; photos: string[]; owner: { site_id?: string; child_id?: string; name: string } | null; mine: boolean;
  claimable: boolean; unclaimed_long: boolean; closed_at: number | null; closed_note: string | null; as: "staff" | "office" | "parent";
  events?: { at: number; by_name: string; kind: string; note: string | null }[]; kids?: { site_id: string; id: string; first: string; last: string }[];
}

export const CATEGORY: Record<string, string> = { clothing: "Clothing", bag: "Bag", bottle: "Water bottle", lunchbox: "Lunch box", book: "Book", toy: "Toy", electronics: "Electronics", glasses: "Glasses", other: "Other" };
const STATUS: Record<Item["status"], { label: string; tone: "info" | "warning" | "success" | "neutral" }> = {
  open: { label: "Whose is it?", tone: "info" }, claimed: { label: "Owner known", tone: "warning" }, returned: { label: "Handed back", tone: "success" }, disposed: { label: "Given away", tone: "neutral" },
};
const EVENT: Record<string, string> = { found: "Found", owner: "Owner named", claimed: "Claimed by a parent", to_school: "Sent to the school office", returned: "Handed back", disposed: "Given away" };
const day = (ts: number, tz?: string) => new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: tz }).format(ts);
const when = (ts: number, tz?: string) => new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: tz }).format(ts);

function Photo({ item, photo, big }: { item: string; photo?: string; big?: boolean }) {
  const url = useBlobUrl(photo ? `/api/v1/lost-found/${item}/photos/${photo}` : null);
  if (!photo) return <span className={`plf-photo plf-photo--none${big ? " plf-photo--big" : ""}`} aria-hidden="true"><Icon name="search" /></span>;
  return url ? <img className={`plf-photo${big ? " plf-photo--big" : ""}`} src={url} alt="" /> : <span className={`plf-photo${big ? " plf-photo--big" : ""}`} aria-hidden="true" />;
}

function Row({ it, onOpen, tz, selected }: { it: Item; onOpen: () => void; tz?: string; selected?: boolean }) {
  const s = STATUS[it.status];
  return (
    <li>
      <button type="button" className="plf-row" onClick={onOpen} aria-current={selected ? "true" : undefined}>
        <Photo item={it.id} photo={it.photos[0]} />
        <span className="plf-row__main">
          <b className="plf-row__title">{it.description}</b>
          <span className="plf-row__sub">{[CATEGORY[it.category], it.on_bus ? `On the bus${it.route ? ` · ${it.route}` : ""}` : it.found_where, day(it.found_at, tz)].filter(Boolean).join(" · ")}</span>
          <span className="plf-row__tags">
            {it.mine ? <Badge tone="warning">{it.owner?.name ?? "Yours"}</Badge> : <Badge tone={s.tone}>{it.status === "claimed" && it.owner ? it.owner.name : s.label}</Badge>}
            {it.unclaimed_long && it.as !== "parent" && <Badge tone="neutral">30+ days</Badge>}
          </span>
        </span>
        <Icon name="chevron-right" />
      </button>
    </li>
  );
}

/** Shrinks a photo to 1600 px (JPEG) before it is sent: a phone's 12 MP photo becomes about 300 KB. */
async function shrink(file: File): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale); canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d")!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error("photo"))), "image/jpeg", 0.8));
  } catch { return file; }
}

// ---------- staff ----------

interface StaffList { items: Item[]; operator: boolean; routes: { id: string; name: string }[]; categories: string[] }

/**
 * The staff list: what this school (or bus company) holds, and logging a new item. `compact` (the
 * console): a heading, a search box and the selected row marked; `version` reloads the list after
 * a change made elsewhere without losing the filter or the search.
 */
export function StaffLostFound({ siteId, tz, open, compact, selectedId, version = 0 }: { siteId: string; tz?: string; open: (id: string) => void; compact?: boolean; selectedId?: string | null; version?: number }) {
  const [data, setData] = useState<StaffList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"open" | "claimed" | "closed">("open");
  const [q, setQ] = useState("");
  const load = useMemo(() => () => api<StaffList>("GET", `/api/v1/sites/${siteId}/lost-found`).then(setData, (e: Error) => setError(e.message)), [siteId]);
  useEffect(() => { void load(); }, [load, version]);
  if (!data) return error ? <Banner tone="danger" title={error} /> : <div className="pmu-center"><Spinner label="Loading" /></div>;
  const inFilter = (i: Item, f: typeof filter) => (f === "closed" ? i.status === "returned" || i.status === "disposed" : i.status === f);
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const matches = (i: Item) => !words.length || words.every((w) => [i.description, CATEGORY[i.category], i.found_where, i.route, i.owner?.name].filter(Boolean).join(" ").toLowerCase().includes(w));
  const shown = data.items.filter((i) => inFilter(i, filter) && matches(i));
  const count = (f: typeof filter) => data.items.filter((i) => inFilter(i, f)).length;
  // A bus company's finds go to the schools on the route, and through them to the parents.
  const who = data.operator ? "the schools on that route and their parents" : "the parents who could own it";
  return (
    <div className={`plf${compact ? " plf--compact" : ""}`}>
      {compact ? (
        <div className="plf__head-row">
          <div><h2 className="plf__title">Lost and found</h2><p className="plf__subtitle">{count("open")} waiting · {count("claimed")} to hand back</p></div>
          <LogItem siteId={siteId} data={data} onDone={(id) => { void load(); setFilter("open"); open(id); }} />
        </div>
      ) : <div className="plf__actions"><LogItem siteId={siteId} data={data} onDone={(id) => { void load(); open(id); }} /></div>}
      {compact && <TextField label="Search" hideLabel type="search" placeholder="Search items, places and owners" value={q} onChange={(e) => setQ(e.target.value)} />}
      <div className="plf__filters" role="tablist" aria-label="Show">
        {([["open", "Whose is it?"], ["claimed", "Owner known"], ["closed", "Done"]] as const).map(([f, label]) => (
          <button key={f} type="button" role="tab" aria-selected={filter === f} className="plf__filter" onClick={() => setFilter(f)}>{label} <span>{count(f)}</span></button>
        ))}
      </div>
      {!shown.length ? <EmptyState icon="search"
        title={words.length ? "Nothing matches" : filter === "open" ? "Nothing waiting for an owner" : filter === "claimed" ? "Nothing to hand back" : "Nothing handed back yet"}
        description={words.length ? "Try another word, or another tab." : filter === "open" ? `Found something${data.operator ? " on a bus" : ""}? Log it with a photo and ${who} will see it.` : undefined} />
        : <ul className="plf-list">{shown.map((it) => <Row key={it.id} it={it} tz={tz} selected={it.id === selectedId} onOpen={() => open(it.id)} />)}</ul>}
    </div>
  );
}

function LogItem({ siteId, data, onDone }: { siteId: string; data: StaffList; onDone: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const blank = { category: "clothing", description: "", found_where: "", route_id: data.routes[0]?.id ?? "" };
  const [f, setF] = useState(blank);
  const [photos, setPhotos] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const [terms] = useTerms();
  const save = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api<{ id: string }>("POST", "/api/v1/lost-found", { site_id: siteId, category: f.category, description: f.description, found_where: f.found_where || null, route_id: data.operator ? f.route_id : undefined });
      for (const ph of photos.slice(0, 3)) await api("POST", `/api/v1/lost-found/${r.id}/photos`, undefined, { raw: await shrink(ph), type: "image/jpeg" });
      setOpen(false); onDone(r.id);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <Dialog title="Log a found item" description={`${data.operator ? "The schools on the route and their parents see it." : "Parents who could own it see it."} Take the photo of the item only, not of a child.`} open={open} onOpenChange={(v) => { setOpen(v); if (v) { setF(blank); setPhotos([]); } }}
      trigger={<Button icon="plus">Log a found item</Button>}
      actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={busy} disabled={f.description.trim().length < 2 || (data.operator && !f.route_id)} onClick={() => void save()}>Save</Button></>}>
      {terms && !terms.accepted ? <RulesCard /> : <>
      <div className="pmu-stack">
        {error && <Banner tone="danger" title={error} />}
        <div className="plf-shots">
          {photos.map((p, i) => <span key={i} className="plf-shot">{p.name.slice(0, 18)}<button type="button" aria-label="Remove the photo" onClick={() => setPhotos((x) => x.filter((_, j) => j !== i))}><Icon name="close" /></button></span>)}
          {photos.length < 3 && <Button variant="secondary" icon="camera" onClick={() => input.current?.click()}>{photos.length ? "Another photo" : "Take a photo"}</Button>}
          <input ref={input} type="file" hidden accept="image/*" capture="environment" onChange={(e) => { const fl = e.target.files?.[0]; if (fl) setPhotos((x) => [...x, fl].slice(0, 3)); e.target.value = ""; }} />
        </div>
        <Select label="What is it" value={f.category} onValueChange={(v) => setF({ ...f, category: v ?? "other" })} options={data.categories.map((c) => ({ value: c, label: CATEGORY[c] ?? c }))} />
        <TextField label="Describe it" required maxLength={200} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Blue water bottle with a dinosaur sticker" />
        {data.operator
          ? <Select label="Bus route" value={f.route_id} onValueChange={(v) => setF({ ...f, route_id: v ?? "" })} options={data.routes.map((r) => ({ value: r.id, label: r.name }))} />
          : <TextField label="Where it was found (optional)" maxLength={80} value={f.found_where} onChange={(e) => setF({ ...f, found_where: e.target.value })} placeholder="Owls classroom, playground, gym" />}
      </div>
          </>}
    </Dialog>
  );
}

// ---------- an item ----------

/** `parent`: the parent app, which sees an item as a parent even when the person also works at the school. */
export function ItemView({ id, tz, onChanged, parent }: { id: string; tz?: string; onChanged?: () => void; parent?: boolean }) {
  const [it, setIt] = useState<Item | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useMemo(() => () => api<{ item: Item }>("GET", `/api/v1/lost-found/${id}${parent ? "?as=parent" : ""}`).then((r) => { setIt(r.item); setError(null); }, (e: Error) => setError(e.message)), [id, parent]);
  useEffect(() => { void load(); }, [load]);
  const done = async (p: Promise<unknown>) => { try { await p; await load(); onChanged?.(); } catch (e) { setError((e as Error).message); } };
  if (!it) return error ? <Banner tone="danger" title={error} /> : <div className="pmu-center"><Spinner label="Loading" /></div>;
  const staff = it.as === "staff";
  const closed = it.status === "returned" || it.status === "disposed";
  return (
    <div className="plf-item">
      {error && <Banner tone="danger" title={error} />}
      <div className="plf-item__hero">
        <div className="plf-item__photos">{it.photos.length ? it.photos.map((p) => <Photo key={p} item={it.id} photo={p} big />) : <Photo item={it.id} big />}</div>
        <div className="plf-item__summary">
          <div className="plf-item__status">
            <Badge tone={STATUS[it.status].tone}>{STATUS[it.status].label}</Badge>
            {it.unclaimed_long && it.as !== "parent" && !closed && <Badge tone="neutral">30+ days</Badge>}
          </div>
          <h2 className="plf-item__title">{it.description}</h2>
          <dl className="plf-item__facts">
            <dt>What</dt><dd>{CATEGORY[it.category] ?? it.category}</dd>
            <dt>Found</dt><dd>{[it.on_bus ? `On the bus${it.route ? ` · ${it.route}` : ""}` : it.found_where, day(it.found_at, tz)].filter(Boolean).join(" · ")}</dd>
            {it.owner && <><dt>Owner</dt><dd><b>{it.owner.name}</b></dd></>}
          </dl>
        </div>
      </div>
      {it.closed_note && <p className="plf-item__note">{it.closed_note}</p>}
      {it.mine && it.status === "claimed" && <Banner tone="info" title="The school knows it is yours">{it.on_bus ? "The bus company will give it back, or send it to the school office." : "Your child gets it back at school, or collect it at the office."}</Banner>}
      {it.claimable && it.kids && <div className="plf-item__acts"><Claim it={it} onClaim={(child, note) => done(api("POST", `/api/v1/lost-found/${it.id}/claim`, { child_id: child, note }, { queue: { label: `Claim for ${it.description}` } }))} /></div>}
      {staff && !closed && (
        <div className="plf-item__acts">
          <NameOwner it={it} onPick={(site, child) => done(api("POST", `/api/v1/lost-found/${it.id}/owner`, { site_id: site, child_id: child }, { queue: { label: `Owner of ${it.description}` } }))} />
          <HandOver it={it} onDone={(how, note) => done(api("POST", `/api/v1/lost-found/${it.id}/return`, { how, note }, { queue: { label: `${it.description} handed back` } }))} />
          {it.status === "open" && <Dispose onDone={(note) => done(api("POST", `/api/v1/lost-found/${it.id}/dispose`, { note }, { queue: { label: `${it.description} cleared` } }))} />}
        </div>
      )}
      {it.events && it.as !== "parent" && (
        <section aria-labelledby="plf-hist" className="plf-item__history">
          <h3 id="plf-hist" className="plf-item__head">History</h3>
          <ol className="plf-hist">{it.events.map((e, i) => (
            <li key={i}>
              <span className="plf-hist__what"><b>{EVENT[e.kind] ?? e.kind}</b>{e.note ? `: ${e.note}` : ""}</span>
              <span className="plf-hist__who">{e.by_name} · {when(e.at, tz)}</span>
            </li>
          ))}</ol>
        </section>
      )}
      <div className="plf-item__report"><ReportDialog path={`/api/v1/lost-found/${it.id}/report`} what="this item" trigger={<Button size="sm" variant="ghost" icon="warning">Report a problem with this item</Button>} /></div>
    </div>
  );
}

function Claim({ it, onClaim }: { it: Item; onClaim: (child: string, note: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [child, setChild] = useState(it.kids![0]?.id ?? "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [terms] = useTerms();
  return (
    <Dialog title="Is it yours?" description="The school (or the bus company) checks and gives it back." open={open} onOpenChange={setOpen}
      trigger={<Button icon="check">This is ours</Button>}
      actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={busy} disabled={!child} onClick={async () => { setBusy(true); await onClaim(child, note); setBusy(false); setOpen(false); }}>Claim it</Button></>}>
      {terms && !terms.accepted ? <RulesCard /> : <div className="pmu-stack">
        <Select label="Whose is it" value={child} onValueChange={(v) => setChild(v ?? "")} options={it.kids!.map((k) => ({ value: k.id, label: `${k.first} ${k.last}` }))} />
        <TextField label="How to tell it is yours (optional)" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Name written inside the collar" />
      </div>}
    </Dialog>
  );
}

function NameOwner({ it, onPick }: { it: Item; onPick: (site: string, child: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [kids, setKids] = useState<{ site_id: string; id: string; first: string; last: string; where: string | null }[] | null>(null);
  const [q, setQ] = useState("");
  useEffect(() => { if (open && !kids) void api<{ children: NonNullable<typeof kids> }>("GET", `/api/v1/lost-found/${it.id}/candidates`).then((r) => setKids(r.children)); }, [open, kids, it.id]);
  const shown = (kids ?? []).filter((k) => `${k.first} ${k.last} ${k.where ?? ""}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 40);
  return (
    <Dialog title="Whose is it?" description="Their parents are told it has been found." open={open} onOpenChange={(v) => { setOpen(v); setQ(""); }}
      trigger={<Button variant={it.status === "open" ? "primary" : "secondary"} icon="child">{it.owner ? "Change the owner" : "Name the owner"}</Button>}
      actions={<DialogClose><Button variant="secondary">Cancel</Button></DialogClose>}>
      <div className="pmu-stack">
        <TextField label="Find a child" value={q} onChange={(e) => setQ(e.target.value)} placeholder="First letters of a name" />
        {!kids ? <Spinner label="Loading" /> : (
          <ul className="plf-pick">{shown.map((k) => <li key={`${k.site_id}:${k.id}`}><button type="button" onClick={async () => { await onPick(k.site_id, k.id); setOpen(false); }}><b>{k.first} {k.last}</b>{k.where && <span className="pmu-muted"> · {k.where}</span>}</button></li>)}</ul>
        )}
      </div>
    </Dialog>
  );
}

function HandOver({ it, onDone }: { it: Item; onDone: (how: string, note: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [how, setHow] = useState(it.on_bus ? "bus" : "child");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const options = it.on_bus
    ? [{ value: "bus", label: "Given back on the bus" }, { value: "parent", label: "Given to a parent at the stop" }, { value: "office", label: "Send it to the school office" }]
    : [{ value: "child", label: "Given to the child at school" }, { value: "parent", label: "Given to a parent" }, { value: "bag", label: "Sent home in the child's bag" }];
  const needsOwner = how === "office" && !it.owner;
  return (
    <Dialog title="Hand it back" description={it.owner ? `To ${it.owner.name}. The parents are told.` : "Name the owner first if you can: the parents are then told."} open={open} onOpenChange={setOpen}
      trigger={<Button variant={it.status === "claimed" ? "primary" : "secondary"} icon="check-circle">Hand it back</Button>}
      actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={busy} disabled={needsOwner} onClick={async () => { setBusy(true); await onDone(how, note); setBusy(false); setOpen(false); }}>Done</Button></>}>
      <div className="pmu-stack">
        <Select label="How" value={how} onValueChange={(v) => setHow(v ?? "child")} options={options} />
        {needsOwner && <Banner tone="info" title="Name the owner first">Then it goes to the right school.</Banner>}
        <TextField label="Note (optional)" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Collected by her dad at 3 pm" />
      </div>
    </Dialog>
  );
}

function Dispose({ onDone }: { onDone: (note: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("Donated");
  return (
    <Dialog title="Nobody claimed it" description="Given away or thrown away: it leaves the list, and the history keeps a note." open={open} onOpenChange={setOpen}
      trigger={<Button variant="ghost">Give it away</Button>}
      actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button onClick={async () => { await onDone(note); setOpen(false); }}>Confirm</Button></>}>
      <TextField label="What happened to it" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} />
    </Dialog>
  );
}

// ---------- parents ----------

interface ParentList { items: Item[]; open: number; mine: number }
let parentCache: ParentList | null = null;

export function ParentLostFound({ open, tz }: { open: (id: string) => void; tz?: string }) {
  const [data, setData] = useState<ParentList | null>(parentCache);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { void api<ParentList>("GET", "/api/v1/guardian/lost-found").then((r) => { parentCache = r; setData(r); }, (e: Error) => setError(e.message)); }, []);
  if (!data) return error ? <Banner tone="danger" title={error} /> : <div className="pmu-center"><Spinner label="Loading" /></div>;
  const mine = data.items.filter((i) => i.mine), others = data.items.filter((i) => !i.mine);
  return (
    <div className="plf">
      <p className="pmu-muted">Things found at your children's school and on their buses. Tap one to say it is yours.</p>
      {mine.length > 0 && <section><h2 className="plf__head">Your children's</h2><ul className="plf-list">{mine.map((it) => <Row key={it.id} it={it} tz={tz} onOpen={() => open(it.id)} />)}</ul></section>}
      {others.length > 0 ? <section><h2 className="plf__head">Waiting for an owner</h2><ul className="plf-list">{others.map((it) => <Row key={it.id} it={it} tz={tz} onOpen={() => open(it.id)} />)}</ul></section>
        : !mine.length && <EmptyState icon="search" title="Nothing lost" description="When the school or the bus finds something, it appears here." />}
    </div>
  );
}

/** The top of the parent app's Messages page: always there, saying what is waiting. */
export function ParentCard({ open }: { open: () => void }) {
  const [data, setData] = useState<ParentList | null>(parentCache);
  useEffect(() => { void api<ParentList>("GET", "/api/v1/guardian/lost-found").then((r) => { parentCache = r; setData(r); }, () => undefined); }, []);
  return (
    <button type="button" className="plf-card" data-news={!!data?.mine || undefined} onClick={open}>
      <span className="plf-card__icon" aria-hidden="true"><Icon name="search" /></span>
      <span className="plf-card__main">
        <b>Lost and found</b>
        <span className="pmu-small pmu-muted">{!data ? "Things found at school and on the bus" : data.mine ? (data.mine === 1 ? "Something of your child's was found" : `${data.mine} of your children's things were found`) : data.open ? `${data.open} ${data.open === 1 ? "thing is" : "things are"} waiting for an owner` : "Nothing waiting right now"}</span>
      </span>
      <Icon name="chevron-right" />
    </button>
  );
}

/** The staff app: a slim bar under Now (or Bus) with what is waiting. */
export function StaffBar({ siteId, open }: { siteId: string; open: () => void }) {
  const [n, setN] = useState<{ open: number; claimed: number } | null>(null);
  useEffect(() => { void api<StaffList>("GET", `/api/v1/sites/${siteId}/lost-found`).then((r) => setN({ open: r.items.filter((i) => i.status === "open").length, claimed: r.items.filter((i) => i.status === "claimed").length }), () => setN(null)); }, [siteId]);
  return (
    <button type="button" className="plf-card plf-card--bar" onClick={open}>
      <span className="plf-card__icon" aria-hidden="true"><Icon name="search" /></span>
      <span className="plf-card__main"><b>Lost and found</b><span className="pmu-small pmu-muted">{n ? (n.claimed ? `${n.claimed} to hand back · ` : "") + `${n.open} waiting for an owner` : "Log something you found"}</span></span>
      <Icon name="chevron-right" />
    </button>
  );
}
